import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  Eye,
  AlertTriangle,
  Truck,
  CheckCircle2,
  X,
  ArrowRight,
  Bell,
  Users,
  RefreshCw,
  UserCheck,
  UserX,
  FileText
} from 'lucide-react';
import io from 'socket.io-client';
import html2pdf from 'html2pdf.js';
import { API_BASE_URL } from '../../api';
import { getApiKey } from '../../utils/auth';
import { initializeOrderSound, playOrderNotificationSound } from '../../utils/orderSound';
import './AdminOrders.css';

const { useCallback } = React;

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `₹${numeric.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const formatTitleCase = (value) => {
  if (!value) return '';
  const str = String(value).replace(/_/g, ' ').toLowerCase();
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const firstNonEmptyText = (...values) => (
  values
    .map((value) => String(value ?? '').trim())
    .find(Boolean) || ''
);

const getProductDescription = (item = {}) => {
  const description = firstNonEmptyText(
    item.product_description,
    item.productDescription,
    item.product_desc,
    item.productDesc,
    item.product_details,
    item.productDetails,
    item.description,
    item.item_description,
    item.itemDescription,
    item.short_description,
    item.shortDescription,
    item.long_description,
    item.longDescription,
    item.meta_description,
    item.metaDescription,
    item.details,
    item.detail,
    item.product?.productDescription,
    item.product?.product_desc,
    item.product?.productDetails,
    item.product?.product_details,
    item.product?.description,
    item.product?.product_description,
    item.product?.shortDescription,
    item.product?.short_description,
    item.product?.longDescription,
    item.product?.long_description,
    item.item?.productDescription,
    item.item?.product_description,
    item.item?.description
  );
  const normalized = description.toLowerCase();
  return ['n/a', 'na', 'null', 'undefined', '-'].includes(normalized) ? '' : description;
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toDisplayNumber = (value) => toFiniteNumber(value) ?? 0;

const getItemIdentityCandidates = (item = {}) => (
  [
    item.id,
    item.order_item_id,
    item.orderItemId,
    item.product_id,
    item.productId,
    item.product?.id,
    item.product?._id,
    item.item?.id,
    item.item?._id,
    firstNonEmptyText(
      item.product_name,
      item.productName,
      item.name,
      item.product?.name,
      item.item?.name
    ).toLowerCase()
  ]
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter(Boolean)
);

const findMatchingDetailItem = (invoiceItem, detailItems, index, usedIndexes) => {
  const invoiceKeys = new Set(getItemIdentityCandidates(invoiceItem));
  const matchingIndex = detailItems.findIndex((detailItem, detailIndex) => (
    !usedIndexes.has(detailIndex) &&
    getItemIdentityCandidates(detailItem).some((key) => invoiceKeys.has(key))
  ));

  if (matchingIndex >= 0) {
    usedIndexes.add(matchingIndex);
    return detailItems[matchingIndex];
  }

  if (detailItems[index] && !usedIndexes.has(index)) {
    usedIndexes.add(index);
    return detailItems[index];
  }

  return null;
};

const alignInvoicePricesWithOrderDetails = (invoice, details) => {
  if (!invoice || typeof invoice !== 'object' || !details || typeof details !== 'object') {
    return invoice;
  }

  const detailItems = Array.isArray(details.items) ? details.items : [];
  const invoiceItems = Array.isArray(invoice.items) ? invoice.items : [];
  const sourceItems = invoiceItems.length ? invoiceItems : detailItems;
  const usedDetailIndexes = new Set();
  const alignedItems = sourceItems.map((item, index) => {
    const detailItem = findMatchingDetailItem(item, detailItems, index, usedDetailIndexes);
    if (!detailItem) return item;

    const quantity = toDisplayNumber(detailItem.quantity);
    const unitPrice = toDisplayNumber(detailItem.unit_price);

    return {
      ...item,
      product_id: item.product_id || detailItem.product_id,
      product_name: item.product_name || detailItem.product_name,
      quantity,
      unit_price: unitPrice,
      total: unitPrice * quantity
    };
  });

  const detailTotal = toFiniteNumber(
    details.total_amount ??
    details.totalAmount ??
    details.grand_total ??
    details.grandTotal
  );

  return {
    ...invoice,
    items: alignedItems,
    ...(detailTotal !== null
      ? {
          grand_total: detailTotal,
          total_amount: detailTotal
        }
      : {})
  };
};

const asObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const normalizeOrderStatus = (value) => {
  const normalized = String(value || '').toLowerCase().trim().replace(/[-\s]+/g, '_');
  const compact = normalized.replace(/_/g, '');

  if (!normalized) return 'placed';
  if (['pending', 'processing', 'orderplaced', 'placed'].includes(compact)) return 'placed';
  if (['accepted', 'assigned', 'partnerassigned', 'deliverypartnerassigned', 'assignedtopartner', 'riderassigned', 'driverassigned'].includes(compact)) return 'assigned';
  if (['picked', 'pickup', 'pickedup', 'orderpickedup'].includes(compact)) return 'picked_up';
  if (['ontheway', 'orderontheway'].includes(compact)) return 'on_the_way';
  if (compact === 'onthewaytopickup') return 'on_the_way_to_pickup';
  if (compact === 'onthewaytodelivery') return 'on_the_way_to_delivery';
  if (['intransit', 'transit', 'orderintransit'].includes(compact)) return 'in_transit';
  if (['outfordelivery', 'ofd'].includes(compact)) return 'out_for_delivery';
  if (['delivered', 'complete', 'completed'].includes(compact)) return 'delivered';
  if (['cancelled', 'canceled', 'cancel'].includes(compact)) return 'cancelled';

  return normalized;
};

const normalizeIncomingOrder = (order) => {
  if (!order || typeof order !== 'object') {
    return order;
  }

  const deliveryOrder = asObject(
    order.deliveryOrder ||
    order.delivery_order ||
    order.delivery ||
    order.deliveryDetails ||
    order.delivery_details ||
    order.deliveryAssignment ||
    order.delivery_assignment
  );
  const status = normalizeOrderStatus(firstNonEmptyText(
    order.deliveryStatus,
    order.delivery_status,
    order.delivery_order_status,
    order.current_status,
    order.orderStatus,
    order.order_status,
    deliveryOrder.deliveryStatus,
    deliveryOrder.delivery_status,
    deliveryOrder.delivery_order_status,
    deliveryOrder.current_status,
    deliveryOrder.order_status,
    deliveryOrder.status,
    order.status
  ));

  return {
    ...order,
    raw_status: order.raw_status || order.status || '',
    status,
    delivery_status: order.delivery_status || deliveryOrder.delivery_status || status
  };
};

const ACTIVE_ORDER_STATUSES = [
  'assigned',
  'confirmed',
  'packing',
  'ready',
  'picked_up',
  'in_transit',
  'on_the_way',
  'on_the_way_to_pickup',
  'on_the_way_to_delivery',
  'out_for_delivery'
];

const COMPLETED_ORDER_STATUSES = ['delivered', 'cancelled', 'rejected'];

const statusMatchesFilter = (status, filter) => {
  const normalizedFilter = normalizeOrderStatus(filter);
  if (normalizedFilter === 'all') return true;

  const normalizedStatus = normalizeOrderStatus(status);
  const groupedFilters = {
    assigned: ['assigned', 'confirmed', 'on_the_way_to_pickup'],
    on_the_way: ['on_the_way', 'on_the_way_to_delivery', 'in_transit', 'out_for_delivery']
  };

  return (groupedFilters[normalizedFilter] || [normalizedFilter]).includes(normalizedStatus);
};

const isOrderCreateAction = (action) => {
  const normalizedAction = String(action || '').toLowerCase().trim();
  return [
    'created',
    'new_order',
    'new-order',
    'order_created',
    'order-created',
    'order_placed',
    'order-placed'
  ].includes(normalizedAction);
};

const shouldPlaySoundForOrder = (order, existsAlready) => {
  if (!order || typeof order !== 'object') {
    return false;
  }

  const status = normalizeOrderStatus(order.status);
  if (isOrderCreateAction(order.action)) {
    return true;
  }

  if (!existsAlready && ['placed', ...ACTIVE_ORDER_STATUSES].includes(status)) {
    return true;
  }

  return false;
};

const hasOrderMeaningfulChange = (previousOrder, nextOrder) => {
  if (!previousOrder || !nextOrder) {
    return false;
  }

  return (
    normalizeOrderStatus(previousOrder.status) !== normalizeOrderStatus(nextOrder.status) ||
    String(previousOrder.payment_status || '') !== String(nextOrder.payment_status || '') ||
    String(previousOrder.updated_at || '') !== String(nextOrder.updated_at || '') ||
    String(previousOrder.sla_status || '') !== String(nextOrder.sla_status || '')
  );
};

const getOrderIdentityKey = (order) => String(order?.id || order?.order_number || '').trim();

const getVendorDisplayName = (vendor) => (
  vendor?.vendor_name ||
  vendor?.shop_name ||
  vendor?.shopName ||
  vendor?.business_name ||
  vendor?.businessName ||
  vendor?.name ||
  vendor?.ownerName ||
  vendor?.full_name ||
  vendor?.owner_name ||
  ''
);

const getVendorAddressText = (vendor) => (
  vendor?.vendor_address ||
  vendor?.shop_address ||
  vendor?.shopAddress ||
  vendor?.address ||
  vendor?.business_address ||
  vendor?.businessAddress ||
  vendor?.pickup_address ||
  vendor?.pickupAddress ||
  ''
);

const getVendorIdentityCandidates = (value) => (
  [
    value?.vendor_id,
    value?.vendorId,
    value?.id,
    value?._id
  ]
    .map((item) => String(item || '').trim())
    .filter(Boolean)
);

const normalizeOrderDetailsPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return normalizeIncomingOrder(payload);
  }

  const orderRoot = (
    payload.order ||
    payload.data?.order ||
    payload.data ||
    payload
  );

  if (!orderRoot || typeof orderRoot !== 'object') {
    return normalizeIncomingOrder(orderRoot);
  }

  const embeddedVendor = (
    orderRoot.vendor ||
    orderRoot.vendor_details ||
    orderRoot.assigned_vendor ||
    orderRoot.seller ||
    payload.vendor ||
    payload.vendor_details ||
    payload.assigned_vendor ||
    payload.seller ||
    payload.data?.vendor ||
    payload.data?.vendor_details ||
    null
  );

  const embeddedCustomer = (
    orderRoot.customer ||
    payload.customer ||
    payload.data?.customer ||
    null
  );

  const items = (
    orderRoot.items ||
    payload.items ||
    payload.data?.items ||
    []
  );

  return normalizeIncomingOrder({
    ...orderRoot,
    items,
    vendor: embeddedVendor || orderRoot.vendor || null,
    vendor_details: orderRoot.vendor_details || embeddedVendor || null,
    assigned_vendor: orderRoot.assigned_vendor || embeddedVendor || null,
    seller: orderRoot.seller || embeddedVendor || null,
    vendor_id:
      orderRoot.vendor_id ||
      orderRoot.vendorId ||
      embeddedVendor?.vendor_id ||
      embeddedVendor?.vendorId ||
      embeddedVendor?.id ||
      embeddedVendor?._id ||
      '',
    vendor_name:
      orderRoot.vendor_name ||
      orderRoot.vendorName ||
      embeddedVendor?.vendor_name ||
      embeddedVendor?.shop_name ||
      embeddedVendor?.shopName ||
      embeddedVendor?.business_name ||
      embeddedVendor?.businessName ||
      '',
    owner_name:
      orderRoot.owner_name ||
      orderRoot.ownerName ||
      embeddedVendor?.owner_name ||
      embeddedVendor?.ownerName ||
      embeddedVendor?.contact_person ||
      embeddedVendor?.contactPerson ||
      '',
    vendor_phone:
      orderRoot.vendor_phone ||
      orderRoot.vendorPhone ||
      embeddedVendor?.phone ||
      embeddedVendor?.mobile ||
      embeddedVendor?.phone_number ||
      embeddedVendor?.phoneNumber ||
      embeddedVendor?.contact_number ||
      embeddedVendor?.contactNumber ||
      embeddedVendor?.owner_phone ||
      embeddedVendor?.ownerPhone ||
      '',
    vendor_email:
      orderRoot.vendor_email ||
      orderRoot.vendorEmail ||
      embeddedVendor?.email ||
      embeddedVendor?.owner_email ||
      embeddedVendor?.ownerEmail ||
      embeddedVendor?.contact_email ||
      embeddedVendor?.contactEmail ||
      '',
    vendor_address:
      orderRoot.vendor_address ||
      orderRoot.shop_address ||
      orderRoot.shopAddress ||
      embeddedVendor?.vendor_address ||
      embeddedVendor?.shop_address ||
      embeddedVendor?.shopAddress ||
      embeddedVendor?.address ||
      '',
    customer_name:
      orderRoot.customer_name ||
      orderRoot.customerName ||
      embeddedCustomer?.name ||
      embeddedCustomer?.customer_name ||
      '',
    customer_phone:
      orderRoot.customer_phone ||
      orderRoot.customerPhone ||
      embeddedCustomer?.phone ||
      embeddedCustomer?.mobile ||
      embeddedCustomer?.phone_number ||
      embeddedCustomer?.phoneNumber ||
      '',
    customer_address:
      orderRoot.customer_address ||
      orderRoot.shipping_address ||
      orderRoot.shippingAddress ||
      embeddedCustomer?.address ||
      embeddedCustomer?.shipping_address ||
      embeddedCustomer?.shippingAddress ||
      '',
    shipping_address:
      orderRoot.shipping_address ||
      orderRoot.shippingAddress ||
      orderRoot.customer_address ||
      embeddedCustomer?.shipping_address ||
      embeddedCustomer?.shippingAddress ||
      embeddedCustomer?.address ||
      ''
  });
};

const isCodPaymentMode = (order) => {
  const mode = String(order?.payment_type || order?.payment_mode || '').toLowerCase().trim();
  return (
    mode === 'cod' ||
    mode === 'cash_on_delivery' ||
    mode === 'cash on delivery' ||
    mode === 'cash'
  );
};

const getDisplayPaymentStatus = (order) => {
  if (!order) return '';
  const orderStatus = normalizeOrderStatus(order.status);
  const backendPaymentStatus = String(order.payment_status || '').toLowerCase();

  if (orderStatus === 'delivered') {
    return 'payment_received';
  }

  if (orderStatus === 'cancelled') {
    if (isCodPaymentMode(order)) {
      return 'payment_not_received';
    }
    return backendPaymentStatus === 'refunded' ? 'refunded' : 'refund_pending';
  }

  return order.payment_status;
};

const ORDER_COLUMNS = [
  { key: 'order_number', label: 'Order #' },
  { key: 'vendor_info', label: 'Vendor' },
  { key: 'customer_name', label: 'Customer' },
  { key: 'customer_phone', label: 'Phone' },
  { key: 'shipping_address', label: 'Address' },
  { key: 'payment_status', label: 'Payment Status', format: (value, order) => formatTitleCase(getDisplayPaymentStatus(order) || value) },
  { key: 'payment_type', label: 'Payment Mode', format: formatTitleCase },
  { key: 'item_count', label: 'Items', format: (value) => Number(value || 0) },
  { key: 'total_quantity', label: 'Qty', format: (value) => Number(value || 0) },
  { key: 'total_amount', label: 'Total', format: formatCurrency },
  { key: 'created_at', label: 'Created', format: formatDateTime }
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDetailsFor, setShowDetailsFor] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [reassignModal, setReassignModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [cancelModal, setCancelModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [soundTestStatus, setSoundTestStatus] = useState('');
  const socketRef = useRef(null);
  const ordersRef = useRef([]);
  const currentPageRef = useRef(1);
  const fetchOrdersRef = useRef(null);
  const recentSoundOrderKeysRef = useRef(new Map());
  const [nowTs, setNowTs] = useState(Date.now());
  const [reassignedIds, setReassignedIds] = useState(new Set());
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [invoiceModal, setInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceExists, setInvoiceExists] = useState({}); // Track which orders have invoices

  const getVendorRecord = (order) => {
    const orderVendorIds = getVendorIdentityCandidates(order);
    if (orderVendorIds.length === 0) {
      return null;
    }

    return vendors.find((vendor) => {
      const vendorIds = getVendorIdentityCandidates(vendor);
      return vendorIds.some((vendorId) => orderVendorIds.includes(vendorId));
    }) || null;
  };

  const getVendorSummary = (order) => {
    const vendorRecord = getVendorRecord(order);
    const embeddedVendor = (
      order?.vendor ||
      order?.vendor_details ||
      order?.assigned_vendor ||
      order?.seller ||
      null
    );
    const vendorName = (
      order?.vendor_name ||
      getVendorDisplayName(embeddedVendor) ||
      getVendorDisplayName(vendorRecord)
    );
    const ownerName = (
      order?.owner_name ||
      order?.ownerName ||
      embeddedVendor?.owner_name ||
      embeddedVendor?.ownerName ||
      embeddedVendor?.contact_person ||
      embeddedVendor?.contactPerson ||
      embeddedVendor?.name ||
      vendorRecord?.owner_name ||
      vendorRecord?.ownerName ||
      ''
    );
    const vendorPhone = (
      order?.vendor_phone ||
      order?.vendorPhone ||
      embeddedVendor?.phone ||
      embeddedVendor?.mobile ||
      embeddedVendor?.phoneNumber ||
      embeddedVendor?.phone_number ||
      embeddedVendor?.contact_number ||
      embeddedVendor?.contactNumber ||
      embeddedVendor?.owner_phone ||
      embeddedVendor?.ownerPhone ||
      vendorRecord?.phone ||
      vendorRecord?.mobile ||
      vendorRecord?.phoneNumber ||
      vendorRecord?.phone_number ||
      vendorRecord?.contact_number ||
      vendorRecord?.contactNumber ||
      vendorRecord?.owner_phone ||
      vendorRecord?.ownerPhone ||
      ''
    );
    const vendorEmail = (
      order?.vendor_email ||
      order?.vendorEmail ||
      embeddedVendor?.email ||
      embeddedVendor?.owner_email ||
      embeddedVendor?.ownerEmail ||
      embeddedVendor?.contact_email ||
      embeddedVendor?.contactEmail ||
      vendorRecord?.email ||
      vendorRecord?.owner_email ||
      vendorRecord?.ownerEmail ||
      vendorRecord?.contact_email ||
      vendorRecord?.contactEmail ||
      ''
    );
    const vendorAddress = (
      order?.vendor_address ||
      order?.shop_address ||
      getVendorAddressText(embeddedVendor) ||
      getVendorAddressText(vendorRecord)
    );

    return {
      vendorName,
      ownerName,
      vendorPhone,
      vendorEmail,
      vendorAddress
    };
  };

  const renderVendorCell = (order) => {
    const { vendorName, ownerName, vendorPhone } = getVendorSummary(order);

    return (
      <div className="vendor-info-cell">
        <span className="vendor-info-name">{vendorName || 'N/A'}</span>
        <span className="vendor-info-meta">ID: {order?.vendor_id || 'N/A'}</span>
        {ownerName && <span className="vendor-info-meta">Owner: {ownerName}</span>}
        {vendorPhone && <span className="vendor-info-meta">{vendorPhone}</span>}
      </div>
    );
  };

  const triggerOrderSound = useCallback((order) => {
    const orderKey = getOrderIdentityKey(order);
    const now = Date.now();
    const recentSoundOrderKeys = recentSoundOrderKeysRef.current;

    recentSoundOrderKeys.forEach((timestamp, key) => {
      if (now - timestamp > 10000) {
        recentSoundOrderKeys.delete(key);
      }
    });

    if (orderKey && recentSoundOrderKeys.has(orderKey)) {
      return false;
    }

    if (orderKey) {
      recentSoundOrderKeys.set(orderKey, now);
    }

    playOrderNotificationSound();
    return true;
  }, []);

  // Fetch all orders (admin view)
  const fetchOrders = async (pageParam = currentPage, options = {}) => {
    try {
      const isBackgroundRefresh = options.background === true;
      const shouldTriggerSound = options.triggerSound === true;
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      const apiKey = getApiKey();
      const params = new URLSearchParams();
      const normalizedFilter = normalizeOrderStatus(filter);
      if (filter !== 'all' && filter && !['assigned', 'on_the_way'].includes(normalizedFilter)) {
        params.append('status', filter);
      }
      if (selectedVendorFilter) params.append('vendor_id', selectedVendorFilter);
      if (paymentStatusFilter) params.append('payment_status', paymentStatusFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom && dateTo) { params.append('date_from', dateFrom); params.append('date_to', dateTo); }
      const pageToFetch = typeof options.page === 'number'
        ? options.page
        : (typeof pageParam === 'number' ? pageParam : 1);
      const limitToUse = typeof options.limit === 'number' ? options.limit : rowsPerPage;
      params.append('page', String(pageToFetch));
      params.append('limit', String(limitToUse));

      const response = await fetch(`${API_BASE_URL}/api/admin/orders?${params.toString()}`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.orders)
            ? data.orders
            : [];
        const normalizedList = list.map(normalizeIncomingOrder);
        const previousOrders = ordersRef.current;

        if (shouldTriggerSound) {
          normalizedList.forEach((order) => {
            const previousOrder = previousOrders.find((existingOrder) => (
              String(existingOrder.id) === String(order.id) ||
              (existingOrder.order_number && existingOrder.order_number === order.order_number)
            ));
            const existsAlready = Boolean(previousOrder);

            if (
              shouldPlaySoundForOrder(order, existsAlready) ||
              hasOrderMeaningfulChange(previousOrder, order)
            ) {
              triggerOrderSound(order);
            }
          });
        }

        setOrders(normalizedList);
        
        // Check invoice existence for delivered orders
        normalizedList.forEach(order => {
          if (order.status === 'delivered') {
            checkInvoiceExists(order.id);
          }
        });
        if (Array.isArray(data)) {
          setPaginationInfo(null);
          setCurrentPage(pageToFetch);
        } else if (data?.pagination) {
          setPaginationInfo(data.pagination);
          const serverPage = parseInt(data.pagination.current_page, 10);
          if (!Number.isNaN(serverPage) && serverPage > 0) {
            setCurrentPage(serverPage);
          } else {
            setCurrentPage(pageToFetch);
          }
        } else {
          setPaginationInfo(null);
          setCurrentPage(pageToFetch);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      if (options.background !== true) {
        setLoading(false);
      }
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}`, {
        headers: { 'x-admin-key': apiKey }
      });
      if (res.ok) {
        const data = await res.json();
        const normalizedDetails = normalizeOrderDetailsPayload(data);
        setOrderDetails(prev => ({ ...prev, [orderId]: normalizedDetails }));
        return normalizedDetails;
      }
    } catch (e) {
      // noop
    }
    return orderDetails[orderId] || null;
  };

  const fetchInvoiceData = async (orderId) => {
    try {
      setLoadingInvoice(true);
      const apiKey = getApiKey();
      console.log('Fetching existing invoice for order:', orderId);
      const detailSnapshot = await fetchOrderDetails(orderId);
      
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/invoice`, {
        headers: { 'x-admin-key': apiKey }
      });
      
      console.log('Fetch invoice response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('Invoice data fetched successfully:', data);
        // Ensure consistent data structure - the GET endpoint returns direct data
        setInvoiceData(alignInvoicePricesWithOrderDetails(data, detailSnapshot));
        setInvoiceModal(true);
      } else {
        const error = await res.json();
        console.error('Fetch invoice error:', error);
        alert(`Error: ${error.error || 'Failed to fetch invoice data'}`);
      }
    } catch (e) {
      console.error('Error fetching invoice data:', e);
      alert('Failed to fetch invoice data');
    } finally {
      setLoadingInvoice(false);
    }
  };

  // Check if invoice exists for an order (silent check)
  const checkInvoiceExists = async (orderId) => {
    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/invoice`, {
        headers: { 'x-admin-key': apiKey }
      });
      
      const exists = res.ok;
      setInvoiceExists(prev => ({ ...prev, [orderId]: exists }));
      return exists;
    } catch (e) {
      setInvoiceExists(prev => ({ ...prev, [orderId]: false }));
      return false;
    }
  };

  const generateInvoice = async (orderId, orderStatus) => {
    console.log('generateInvoice called with:', { orderId, orderStatus });

    try {
      setGeneratingInvoice(true);
      const apiKey = getApiKey();
      const detailSnapshot = await fetchOrderDetails(orderId);
      
      // First, check if invoice already exists
      console.log('Checking if invoice exists for order:', orderId);
      const checkRes = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/invoice`, {
        method: 'GET',
        headers: { 'x-admin-key': apiKey }
      });
      
      if (checkRes.ok) {
        // Invoice exists, fetch and display it
        const existingInvoice = await checkRes.json();
        console.log('Found existing invoice:', existingInvoice);
        console.log('Invoice data keys:', Object.keys(existingInvoice));
        setInvoiceData(alignInvoicePricesWithOrderDetails(existingInvoice, detailSnapshot));
        setInvoiceModal(true);
        setInvoiceExists(prev => ({ ...prev, [orderId]: true }));
        return;
      }
      
      // Invoice doesn't exist, generate new one
      console.log('Invoice not found, generating new invoice for order:', orderId);
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/generate-invoice`, {
        method: 'POST',
        headers: { 'x-admin-key': apiKey }
      });
      
      console.log('Invoice generation response status:', res.status);
      
      if (res.ok) {
        const data = await res.json();
        console.log('Invoice generated successfully:', data);
        console.log('Generated invoice data keys:', Object.keys(data.invoice || {}));
        // Handle nested response structure from generate endpoint
        setInvoiceData(alignInvoicePricesWithOrderDetails(data.invoice, detailSnapshot));
        setInvoiceModal(true);
        // Mark invoice as existing
        setInvoiceExists(prev => ({ ...prev, [orderId]: true }));
        // Refresh orders list to update button state
        fetchOrders(currentPage);
      } else {
        const error = await res.json();
        console.error('Invoice generation error:', error);
        
        // If invoice already exists (400 error), try to fetch it
        if (res.status === 400 && error.error && error.error.includes('Invoice already exists')) {
          console.log('Invoice already exists, attempting to fetch existing invoice...');
          try {
            const fetchRes = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/invoice`, {
              method: 'GET',
              headers: { 'x-admin-key': apiKey }
            });
            
            if (fetchRes.ok) {
              const existingInvoice = await fetchRes.json();
              console.log('Successfully fetched existing invoice after 400 error:', existingInvoice);
              setInvoiceData(alignInvoicePricesWithOrderDetails(existingInvoice, detailSnapshot));
              setInvoiceModal(true);
              setInvoiceExists(prev => ({ ...prev, [orderId]: true }));
              return;
            }
          } catch (fetchError) {
            console.error('Failed to fetch existing invoice after 400 error:', fetchError);
          }
        }
        
        alert(`Failed to generate invoice: ${error.error || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error with invoice operation:', e);
      alert('Failed to process invoice request');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Fetch vendors for reassignment
  const fetchVendors = async () => {
    try {
      const apiKey = getApiKey();
      const response = await fetch(`${API_BASE_URL}/api/admin/vendors`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const vendors = data.items || data.vendors || (Array.isArray(data) ? data : []);
        setVendors(vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    fetchOrdersRef.current = fetchOrders;
  }, [fetchOrders]);

  useEffect(() => initializeOrderSound(), []);

  useEffect(() => {
    const apiKey = getApiKey();
    if (apiKey) {
      const socketUrl = API_BASE_URL || window.location.origin;
      const newSocket = io(socketUrl, {
        auth: { token: apiKey, type: 'admin' }
      });
      
      newSocket.on('connect', () => {
        console.log('Admin connected to WebSocket');
        newSocket.emit('join-admin');
      });
      
      newSocket.on('order-update', (orderData) => {
        const normalizedOrder = normalizeIncomingOrder(orderData);
        const orderKey = String(normalizedOrder.id || normalizedOrder.order_number || '').trim();
        const previousOrder = ordersRef.current.find(order => (
          String(order.id) === String(normalizedOrder.id) ||
          (order.order_number && order.order_number === normalizedOrder.order_number)
        ));
        const existsAlready = Boolean(previousOrder);

        if (
          shouldPlaySoundForOrder(normalizedOrder, existsAlready) ||
          hasOrderMeaningfulChange(previousOrder, normalizedOrder) ||
          ['status_updated', 'reassigned'].includes(String(normalizedOrder.action || '').toLowerCase())
        ) {
          const now = Date.now();
          const recentSoundOrderKeys = recentSoundOrderKeysRef.current;

          recentSoundOrderKeys.forEach((timestamp, key) => {
            if (now - timestamp > 10000) {
              recentSoundOrderKeys.delete(key);
            }
          });

          if (!orderKey || !recentSoundOrderKeys.has(orderKey)) {
            if (orderKey) {
              recentSoundOrderKeys.set(orderKey, now);
            }
            playOrderNotificationSound();
          }
        }

        console.log('Admin order update received:', normalizedOrder);
        
        // Add notification for important updates
        if (normalizedOrder.action === 'status_updated' || normalizedOrder.action === 'reassigned') {
          const message = normalizedOrder.action === 'reassigned' 
            ? `Order ${normalizedOrder.order_number} reassigned to new vendor`
            : `Order ${normalizedOrder.order_number} status updated to ${normalizedOrder.status}`;
          
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'order_update',
            message,
            orderData: normalizedOrder,
            timestamp: new Date()
          }]);
        }
        
        // Update orders list immediately so UI changes without manual refresh.
        if (normalizedOrder.action === 'reassigned') {
          setOrders(prev => {
            const exists = prev.some(o => String(o.id) === String(normalizedOrder.id));
            if (exists) {
              return prev.map(order => (
                String(order.id) === String(normalizedOrder.id)
                  ? { ...order, ...normalizedOrder }
                  : order
              ));
            }
            return [normalizedOrder, ...prev];
          });
          setReassignedIds(prev => {
            const next = new Set(prev);
            next.add(normalizedOrder.id);
            return next;
          });
          // Auto-clear highlight after 10 minutes
          setTimeout(() => {
            setReassignedIds(prev => {
              const next = new Set(prev);
              next.delete(normalizedOrder.id);
              return next;
            });
          }, 10 * 60 * 1000);
        } else {
          setOrders(prev => {
            const existingIndex = prev.findIndex(order => String(order.id) === String(normalizedOrder.id));

            if (existingIndex >= 0) {
              const next = [...prev];
              next[existingIndex] = { ...next[existingIndex], ...normalizedOrder };
              return next;
            }

            if (!normalizedOrder.id && !normalizedOrder.order_number) {
              return prev;
            }

            return [normalizedOrder, ...prev];
          });
        }

        // Keep pagination/counts in sync after the immediate UI update.
        window.setTimeout(() => {
          if (fetchOrdersRef.current) {
            fetchOrdersRef.current(currentPageRef.current);
          }
        }, 0);
      });
      
      setSocket(newSocket);
      socketRef.current = newSocket;
    }
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Polling fallback when socket is not connected
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrders(currentPageRef.current, { background: true, triggerSound: true });
    }, 8 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Tick timer to refresh SLA countdowns
  useEffect(() => {
    const interval = setInterval(() => setNowTs(Date.now()), 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchOrders(1);
    fetchVendors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchOrders(1);
  }, [filter, selectedVendorFilter, paymentStatusFilter, dateFrom, dateTo]);

  // Reassign order to different vendor
  const reassignOrder = async () => {
    if (!selectedOrder || !selectedVendor) {
      alert('Please select a vendor for reassignment');
      return;
    }

    try {
      const apiKey = getApiKey();
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${selectedOrder.id}/reassign`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vendor_id: selectedVendor,
          reason: reassignReason
        })
      });
      
      if (response.ok) {
        setReassignModal(false);
        setSelectedOrder(null);
        setSelectedVendor('');
        setReassignReason('');
        fetchOrders(currentPage);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reassign order');
      }
    } catch (error) {
      console.error('Error reassigning order:', error);
      alert('Failed to reassign order');
    }
  };

  // Update order status (admin override)
  const updateOrderStatus = async (orderId, status) => {
    try {
      const apiKey = getApiKey();
      const response = await fetch(`${API_BASE_URL}/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        fetchOrders(currentPage);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const confirmCancel = async () => {
    if (!selectedOrder) return;
    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${selectedOrder.id}/cancel`, {
        method: 'PUT',
        headers: { 'x-admin-key': apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason })
      });
      if (res.ok) {
        setCancelModal(false);
        setCancelReason('');
        setSelectedOrder(null);
        fetchOrders(currentPage);
      } else {
        const e = await res.json();
        alert(e.error || 'Failed to cancel order');
      }
    } catch (e) {
      console.error('Cancel order error', e);
    }
  };

  const confirmRefund = async () => {
    if (!selectedOrder) return;
    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/orders/${selectedOrder.id}/refund`, {
        method: 'PUT',
        headers: { 'x-admin-key': apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: refundReason })
      });
      if (res.ok) {
        setRefundModal(false);
        setRefundReason('');
        setSelectedOrder(null);
        fetchOrders(currentPage);
      } else {
        const e = await res.json();
        alert(e.error || 'Failed to refund order');
      }
    } catch (e) {
      console.error('Refund order error', e);
    }
  };

  const handleTestSound = async () => {
    setSoundTestStatus('');
    const didPlay = await playOrderNotificationSound();
    setSoundTestStatus(didPlay ? 'Sound test played' : 'Browser blocked audio on this page');

    window.setTimeout(() => {
      setSoundTestStatus('');
    }, 3000);
  };

  const getStatusBadge = (status, slaStatus) => {
    const normalizedStatus = normalizeOrderStatus(status);
    const statusClasses = {
      'placed': slaStatus === 'sla_breached' ? 'status-sla-breached' : 
               slaStatus === 'sla_warning' ? 'status-sla-warning' : 'status-placed',
      'assigned': 'status-assigned',
      'confirmed': 'status-confirmed',
      'packing': 'status-packing',
      'ready': 'status-ready',
      'picked_up': 'status-picked-up',
      'in_transit': 'status-in-transit',
      'on_the_way': 'status-on-the-way',
      'on_the_way_to_pickup': 'status-assigned',
      'on_the_way_to_delivery': 'status-on-the-way',
      'out_for_delivery': 'status-out-for-delivery',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled',
      'rejected': 'status-rejected',
      'reassigned': 'status-reassigned'
    };
    return statusClasses[normalizedStatus] || 'status-default';
  };

  const getStatusIcon = (status) => {
    switch (normalizeOrderStatus(status)) {
      case 'placed': return <Clock size={16} />;
      case 'assigned': return <UserCheck size={16} />;
      case 'confirmed': return <CheckCircle size={16} />;
      case 'packing': return <Package size={16} />;
      case 'ready': return <CheckCircle2 size={16} />;
      case 'picked_up': return <Package size={16} />;
      case 'in_transit': return <Truck size={16} />;
      case 'on_the_way': return <Truck size={16} />;
      case 'on_the_way_to_pickup': return <UserCheck size={16} />;
      case 'on_the_way_to_delivery': return <Truck size={16} />;
      case 'out_for_delivery': return <Truck size={16} />;
      case 'delivered': return <CheckCircle2 size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      case 'rejected': return <X size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    const normalizedStatus = normalizeOrderStatus(status);
    const statusTexts = {
      'placed': 'Order Placed',
      'assigned': 'Partner Assigned',
      'confirmed': 'Partner Assigned',
      'packing': 'Packing',
      'ready': 'Ready',
      'picked_up': 'Picked Up',
      'in_transit': 'Order On The Way',
      'on_the_way': 'Order On The Way',
      'on_the_way_to_pickup': 'Partner Assigned',
      'on_the_way_to_delivery': 'Order On The Way',
      'out_for_delivery': 'Order On The Way',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled',
      'rejected': 'Rejected',
      'reassigned': 'Reassigned'
    };
    return statusTexts[normalizedStatus] || formatTitleCase(normalizedStatus);
  };

  const getStatusBadgeContent = (status) => {
    const normalizedStatus = normalizeOrderStatus(status);
    // For these statuses, show text only (no icons)
    if (['placed', 'delivered', 'cancelled'].includes(normalizedStatus)) {
      return getStatusText(normalizedStatus);
    }
    // For other statuses, show icon + text
    return (
      <>
        {getStatusIcon(normalizedStatus)}
        {getStatusText(normalizedStatus)}
      </>
    );
  };

  const formatRemaining = (deadline) => {
    const ms = new Date(deadline).getTime() - nowTs;
    if (Number.isNaN(ms)) return '';
    if (ms <= 0) return 'SLA Breached';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `SLA: ${minutes}m ${seconds.toString().padStart(2, '0')}s left`;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (order.vendor_name && order.vendor_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (order.shipping_address && order.shipping_address.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filter === 'all' || statusMatchesFilter(order.status, filter);
    return matchesSearch && matchesFilter;
  });

  const pendingOrders = orders.filter(order => normalizeOrderStatus(order.status) === 'placed');
  const activeOrders = orders.filter(order => ACTIVE_ORDER_STATUSES.includes(normalizeOrderStatus(order.status)));
  const completedOrders = orders.filter(order => COMPLETED_ORDER_STATUSES.includes(normalizeOrderStatus(order.status)));
  const visibleOrders = activeTab === 'pending'
    ? pendingOrders
    : activeTab === 'active'
      ? activeOrders
      : activeTab === 'completed'
        ? completedOrders
        : filteredOrders;
  const activeOrderDetails = selectedOrderDetails
    ? {
        ...selectedOrderDetails,
        ...(orderDetails[selectedOrderDetails.id] || {})
      }
    : null;
  const activeVendorSummary = activeOrderDetails ? getVendorSummary(activeOrderDetails) : null;
  const totalOrders = paginationInfo?.total_orders ?? orders.length;
  const totalPages = paginationInfo?.total_pages ?? (orders.length > 0 ? 1 : 0);

  return (
    <div className="admin-orders">
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="notifications-container">
          {notifications.map(notification => (
            <div key={notification.id} className="notification">
              <Bell size={16} />
              <span>{notification.message}</span>
              <button onClick={() => dismissNotification(notification.id)}>
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="admin-orders-header">
        <div className="header-content">
          <h1>Order Management</h1>
          <p>Monitor and manage all orders across vendors with real-time updates</p>
        </div>
        <div className="header-actions">
          <div className="order-stats">
            <div className="stat-item">
              <span className="stat-number">{pendingOrders.length}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{activeOrders.length}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{completedOrders.length}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={handleTestSound}>
            <Bell size={18} />
            Test Sound
          </button>
          <button className="btn btn-secondary" onClick={() => fetchOrders(currentPage)}>
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>
      {soundTestStatus && (
        <div className="sound-test-status">{soundTestStatus}</div>
      )}
      <button
        type="button"
        className="floating-sound-test-btn"
        onClick={handleTestSound}
      >
        <Bell size={18} />
        Test Sound
      </button>

      {/* Tabs */}
      <div className="admin-orders-tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <Eye size={16} />
          All Orders ({orders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <Bell size={16} />
          Pending ({pendingOrders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <Package size={16} />
          Active ({activeOrders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <CheckCircle size={16} />
          Completed ({completedOrders.length})
        </button>
      </div>

      <div className="admin-orders-filters">
        <div className="filters-left">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search orders, vendors, addresses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchOrders(1);
                }
              }}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <Filter size={20} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="placed">Order Placed</option>
              <option value="assigned">Partner Assigned</option>
              <option value="packing">Packing</option>
              <option value="ready">Ready</option>
              <option value="picked_up">Picked Up</option>
              <option value="on_the_way">Order On The Way</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter size={20} />
            <select
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter size={20} />
            <select
              value={selectedVendorFilter}
              onChange={(e) => setSelectedVendorFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{getVendorDisplayName(v) || `Vendor ${v.id}`}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filters-right">
          <div className="date-range">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span style={{ margin: '0 4px' }}>to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="results-count">
            {totalOrders > 0
              ? `${visibleOrders.length} of ${totalOrders} orders`
              : '0 orders'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : (
        <div className="admin-orders-list">
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  {ORDER_COLUMNS.map(({ key, label }) => (
                    <th key={key}>{label}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((order) => (
                  <tr key={order.id} className={reassignedIds.has(order.id) ? 'order-reassigned' : ''}>
                    {ORDER_COLUMNS.map(({ key, format }) => (
                      <td key={key}>
                        {key === 'vendor_info'
                          ? renderVendorCell(order)
                          : format
                            ? format(order[key], order)
                            : (order[key] ?? '')}
                      </td>
                    ))}
                    <td className="actions-cell">
                      <div className="actions-container">
                        <div className="status-badge-container">
                          <span className={`status-badge ${getStatusBadge(order.status, order.sla_status)}`}>
                            {getStatusBadgeContent(order.status)}
                          </span>
                          {order.sla_deadline && order.status === 'placed' && (
                            <div className="sla-info">
                              <Clock size={12} />
                              <span>{formatRemaining(order.sla_deadline)}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="action-buttons">
                          {/* Only show invoice button for delivered orders */}
                          {order.status === 'delivered' && (
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                console.log('View Invoice button clicked for order:', order.id, 'Status:', order.status);
                                // Reset previous invoice data and modal state to prevent stale state
                                setInvoiceData(null);
                                setInvoiceModal(false);
                                // Call generateInvoice which handles both generation and viewing
                                generateInvoice(order.id, order.status);
                              }}
                              disabled={loadingInvoice || generatingInvoice}
                              title="View Invoice - Generate if not exists"
                            >
                              <FileText size={16} />
                              {generatingInvoice ? 'Processing...' : loadingInvoice ? 'Loading...' : 'View Invoice'}
                            </button>
                          )}
                          
                          {['confirmed', 'packing', 'ready', 'out_for_delivery'].includes(order.status) && (
                            <button 
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                const nextStatus = getNextStatus(order.status);
                                if (nextStatus) {
                                  updateOrderStatus(order.id, nextStatus);
                                }
                              }}
                              title="Update Status"
                            >
                              <ArrowRight size={16} />
                              Update
                            </button>
                          )}
                          
                          {/* Refund button - commented out for now */}
                          {/* {order.payment_status !== 'refunded' && (
                            <button 
                              className="btn btn-warning btn-sm"
                              onClick={() => { setSelectedOrder(order); setRefundModal(true); }}
                              title="Refund Payment"
                            >
                              <CheckCircle2 size={16} />
                              Refund
                            </button>
                          )} */}
                          
                          <button 
                            className="btn btn-info btn-sm" 
                            onClick={async () => {
                              setSelectedOrderDetails(order);
                              setDetailsModal(true);
                              await fetchOrderDetails(order.id);
                            }}
                            title="View Details"
                          >
                            <Eye size={16} />
                            Details
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {visibleOrders.length === 0 && (
            <div className="empty-state">
              <ShoppingCart size={48} />
              <h3>No orders found</h3>
              <p>No orders match your current filters</p>
            </div>
          )}
          {visibleOrders.length > 0 && (
            <div className="pagination-container" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Showing {visibleOrders.length} of {totalOrders} orders (page {currentPage} of {Math.max(totalPages, 1)})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage > 1) {
                      const prevPage = currentPage - 1;
                      setCurrentPage(prevPage);
                      fetchOrders(prevPage);
                    }
                  }}
                  disabled={currentPage <= 1}
                >
                  Prev
                </button>
                {(() => {
                  const pages = [];
                  const maxButtons = 7;
                  if (totalPages <= maxButtons) {
                    for (let i = 1; i <= totalPages; i += 1) {
                      pages.push(i);
                    }
                  } else {
                    const setPages = new Set([1, totalPages, currentPage]);
                    for (let i = 1; i <= 2; i += 1) {
                      setPages.add(currentPage - i);
                      setPages.add(currentPage + i);
                    }
                    const sortedPages = Array.from(setPages)
                      .filter(page => page >= 1 && page <= totalPages)
                      .sort((a, b) => a - b);

                    let lastPage = 0;
                    sortedPages.forEach((page) => {
                      if (lastPage && page - lastPage > 1) {
                        pages.push('ellipsis');
                      }
                      pages.push(page);
                      lastPage = page;
                    });
                  }
                  return pages.map((page, index) => {
                    if (page === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${index}`} style={{ padding: '4px 8px', color: '#999' }}>…</span>
                      );
                    }
                    return (
                      <button
                        key={`page-${page}`}
                        className={`btn btn-secondary btn-sm ${page === currentPage ? 'active' : ''}`}
                        style={page === currentPage ? { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' } : {}}
                        onClick={() => {
                          if (page !== currentPage) {
                            setCurrentPage(page);
                            fetchOrders(page);
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      const nextPage = currentPage + 1;
                      setCurrentPage(nextPage);
                      fetchOrders(nextPage);
                    }
                  }}
                  disabled={currentPage >= totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#666' }}>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(newSize);
                    setCurrentPage(1);
                    fetchOrders(1, { limit: newSize, page: 1 });
                  }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Order Details Modal */}
      {detailsModal && activeOrderDetails && (
        <div className="modal-overlay">
          <div className="details-modal">
            <div className="details-modal-header">
              <div className="header-left">
                <h2>Order Details</h2>
                <span className="order-number">#{activeOrderDetails.order_number}</span>
              </div>
              <button className="close-btn" onClick={() => setDetailsModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="details-modal-body">
              {/* Order Summary Card */}
              <div className="details-card">
                <div className="card-header">
                  <Package size={20} />
                  <h3>Order Summary</h3>
                </div>
                <div className="card-content">
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Order Number</span>
                        <span className="value">{activeOrderDetails.order_number}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Vendor</span>
                        <span className="value">{activeVendorSummary?.vendorName || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Vendor ID</span>
                        <span className="value">{activeOrderDetails.vendor_id || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Owner / Contact Person</span>
                        <span className="value">{activeVendorSummary?.ownerName || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Status</span>
                        <span className={`status-badge ${getStatusBadge(activeOrderDetails.status, activeOrderDetails.sla_status)}`}>
                          {getStatusBadgeContent(activeOrderDetails.status)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Total Amount</span>
                      <span className="value amount">₹{Number(activeOrderDetails.total_amount || 0).toFixed(2)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Payment Status</span>
                      <span className="value">{formatTitleCase(getDisplayPaymentStatus(activeOrderDetails))}</span>
                    </div>
                      <div className="detail-item">
                        <span className="label">Payment Mode</span>
                        <span className="value">{formatTitleCase(activeOrderDetails.payment_type || activeOrderDetails.payment_mode)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Vendor Phone</span>
                        <span className="value">{activeVendorSummary?.vendorPhone || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Vendor Email</span>
                        <span className="value">{activeVendorSummary?.vendorEmail || 'N/A'}</span>
                      </div>
                      <div className="detail-item full-width">
                        <span className="label">Vendor Address</span>
                        <span className="value">{activeVendorSummary?.vendorAddress || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Customer Information Card */}
              <div className="details-card">
                <div className="card-header">
                  <Users size={20} />
                  <h3>Customer Information</h3>
                </div>
                <div className="card-content">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Customer Name</span>
                      <span className="value">{activeOrderDetails.customer_name || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Phone Number</span>
                      <span className="value">{activeOrderDetails.customer_phone || 'N/A'}</span>
                    </div>
                    <div className="detail-item full-width">
                      <span className="label">Shipping Address</span>
                      <span className="value">{activeOrderDetails.shipping_address || activeOrderDetails.customer_address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items Card */}
              {orderDetails[activeOrderDetails.id] && (
                <div className="details-card">
                  <div className="card-header">
                    <ShoppingCart size={20} />
                    <h3>Order Items</h3>
                  </div>
                  <div className="card-content">
                    <div className="items-table">
                      <div className="items-header">
                        <span>Product</span>
                        <span>Quantity</span>
                        <span>Unit Price</span>
                        <span>Total</span>
                      </div>
                      {(orderDetails[activeOrderDetails.id].items || []).map((item, index) => {
                        const productDescription = getProductDescription(item);
                        return (
                        <div key={item.id || `${item.product_id}-${index}`} className="item-row">
                          <span className="item-name">
                            <span className="item-product-title">{item.product_name || `Product #${item.product_id}`}</span>
                            {productDescription ? <span className="item-product-description">{productDescription}</span> : null}
                          </span>
                          <span className="item-qty">×{item.quantity}</span>
                          <span className="item-price">₹{Number(item.unit_price || 0).toFixed(2)}</span>
                          <span className="item-total">₹{Number((item.unit_price || 0) * (item.quantity || 0)).toFixed(2)}</span>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps Card */}
              <div className="details-card">
                <div className="card-header">
                  <Clock size={20} />
                  <h3>Timeline</h3>
                </div>
                <div className="card-content">
                  <div className="detail-grid">
                      <div className="detail-item">
                        <span className="label">Order Created</span>
                      <span className="value">{formatDateTime(activeOrderDetails.created_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Last Updated</span>
                      <span className="value">{formatDateTime(activeOrderDetails.updated_at)}</span>
                    </div>
                    {activeOrderDetails.sla_deadline && (
                      <div className="detail-item">
                        <span className="label">SLA Deadline</span>
                        <span className="value">{formatDateTime(activeOrderDetails.sla_deadline)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {cancelModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Cancel Order</h2>
              <button className="close-btn" onClick={() => setCancelModal(false)}>Ã—</button>
            </div>
            <div className="modal-body">
              <p>Order: <strong>{selectedOrder.order_number}</strong></p>
              <div className="form-group">
                <label>Reason for Cancellation</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows="3"
                  placeholder="Enter reason for cancellation..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setCancelModal(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={confirmCancel}>Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Order Modal */}
      {refundModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Refund Payment</h2>
              <button className="close-btn" onClick={() => setRefundModal(false)}>Ã—</button>
            </div>
            <div className="modal-body">
              <p>Order: <strong>{selectedOrder.order_number}</strong></p>
              <div className="form-group">
                <label>Reason for Refund</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows="3"
                  placeholder="Enter reason for refund..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setRefundModal(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={confirmRefund}>Confirm Refund</button>
            </div>
          </div>
        </div>
      )}

      {/* Professional Invoice Modal */}
      {invoiceModal && invoiceData && (
        <div className="modal-overlay">
          <div className="professional-invoice-modal">
            <div className="invoice-close-btn">
              <button className="close-btn" onClick={() => {
                setInvoiceModal(false);
                setInvoiceData(null); // Clear data when closing
              }}>
                <X size={24} />
              </button>
            </div>
            
            <div className="professional-invoice-container" id="invoice-content">
              {/* Invoice Header */}
              <div className="invoice-letterhead">
                <div className="invoice-title">
                  <h1>INVOICE</h1>
                </div>
                <div className="invoice-header-details">
                  <div className="invoice-meta">
                    <div className="meta-item">
                      <span className="meta-label">Invoice No:</span>
                      <span className="meta-value">{invoiceData.invoice_number}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Invoice Date:</span>
                      <span className="meta-value">{invoiceData.invoice_date}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Order ID:</span>
                      <span className="meta-value">{invoiceData.order_id}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Seller and Buyer Details */}
              <div className="invoice-parties-section">
                <div className="party-details-container">
                  <div className="seller-details">
                    <h3>Seller Details</h3>
                    <div className="party-info">
                      <div className="info-line">
                        <strong>Name:</strong> {invoiceData.vendor_name || 'N/A'}
                      </div>
                      <div className="info-line">
                        <strong>Address:</strong> {invoiceData.vendor_address || 'N/A'}
                      </div>
                    </div>
                  </div>

                  <div className="buyer-details">
                    <h3>Buyer Details</h3>
                    <div className="party-info">
                      <div className="info-line">
                        <strong>Name:</strong> {invoiceData.customer_name || 'N/A'}
                      </div>
                      <div className="info-line">
                        <strong>Address:</strong> {invoiceData.customer_address || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="invoice-items-section">
                <table className="professional-invoice-table">
                  <thead>
                    <tr>
                      <th>S.No</th>
                      <th>Item Name</th>
                      <th>Qty</th>
                      <th>Unit Price (₹)</th>
                      <th>Total (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoiceData.items || []).map((item, index) => {
                      const productDescription = getProductDescription(item);
                      return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td className="item-description">
                          <span className="invoice-product-name">{item.product_name || 'N/A'}</span>
                          {productDescription ? <span className="invoice-product-description">{productDescription}</span> : null}
                        </td>
                        <td>{item.quantity || 0}</td>
                        <td>{(item.unit_price || 0).toFixed(2)}</td>
                        <td className="item-total">{(item.total || 0).toFixed(2)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Amount Summary */}
              <div className="invoice-summary">
                <div className="summary-table">
                  <div className="summary-row total-row">
                    <span className="summary-label">Grand Total:</span>
                    <span className="summary-value">₹{(invoiceData.grand_total || invoiceData.total_amount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Amount in Words */}
              <div className="amount-in-words-section">
                <div className="amount-words">
                  <strong>Amount in Words:</strong> {invoiceData.amount_in_words || 'N/A'}
                </div>
              </div>

              {/* Invoice Footer with Disclaimers */}
              <div className="invoice-footer-section">
                <div className="invoice-disclaimers">
                  <div className="disclaimer-item">
                    <strong>Note:</strong> This invoice is issued by the selling vendor.
                  </div>
                  <div className="disclaimer-item">
                    SonicKart acts only as a marketplace facilitator and is not the seller.
                  </div>
                  <div className="disclaimer-item">
                    This is a system-generated invoice and does not require a physical signature.
                  </div>
                </div>
                
                <div className="invoice-generation-info">
                  <div className="generation-line">
                    <strong>Generated on:</strong> {new Date(invoiceData.created_at).toLocaleString()}
                  </div>
                  <div className="generation-line">
                    <strong>System Generated Invoice</strong> - No signature required
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="invoice-actions">
              <button type="button" className="btn btn-secondary" onClick={() => {
                setInvoiceModal(false);
                setInvoiceData(null); // Clear data when closing
              }}>
                Close
              </button>
              <button type="button" className="btn btn-success" onClick={() => {
                const element = document.getElementById('invoice-content');
                const opt = {
                  margin: [0.5, 0.5, 0.5, 0.5],
                  filename: `Invoice_${invoiceData.invoice_number.replace(/\//g, '_')}.pdf`,
                  image: { type: 'jpeg', quality: 0.98 },
                  html2canvas: { 
                    scale: 2,
                    useCORS: true,
                    letterRendering: true
                  },
                  jsPDF: { 
                    unit: 'in', 
                    format: 'a4', 
                    orientation: 'portrait',
                    compress: true
                  }
                };
                
                // Show loading state
                const button = event.target;
                const originalText = button.textContent;
                button.textContent = 'Generating PDF...';
                button.disabled = true;
                
                html2pdf().set(opt).from(element).save().then(() => {
                  // Reset button state
                  button.textContent = originalText;
                  button.disabled = false;
                }).catch((error) => {
                  console.error('PDF generation failed:', error);
                  alert('Failed to generate PDF. Please try again.');
                  button.textContent = originalText;
                  button.disabled = false;
                });
              }}>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get next status
const getNextStatus = (currentStatus) => {
  const statusFlow = {
    'confirmed': 'packing',
    'packing': 'ready',
    'ready': 'out_for_delivery',
    'out_for_delivery': 'delivered'
  };
  return statusFlow[currentStatus] || '';
};

export default AdminOrders;
